import { useState } from "react";
import { useFieldArray } from "react-hook-form";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import TextField from "../../../Form/TextField";
import {
  SITE_LINK_DESCRIPTION_PLACEHOLDER,
  SITE_LINK_FINAL_URL_PLACEHOLDER,
  SITE_LINK_PLACEHOLDER,
  SITE_LINK_TEXT_PLACEHOLDER,
  SITE_LINKS_PLACEHOLDER,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  ADS_CAMPAIGN_GOOGLE_SITE_LINK_DESCRIPTION_MAX_LENGTH,
  ADS_CAMPAIGN_GOOGLE_SITE_LINK_NAME_MAX_LENGTH,
  BORDER_RADIUS,
} from "../../../../utils/constants";
import { getReactHookFormNestedError } from "../../../../utils/errors";
import {
  BOX_SHADOW_THEME,
  INPUT_COLOR_BORDER,
  RED_COLOR,
} from "../../../../utils/colors";
import ChipOptional from "../../../Chip/ChipOptional";
import { ChipAIGeneratedSection } from "../../../Chip/ChipAIGenerated";

const accordionSx = {
  border: `1px solid ${INPUT_COLOR_BORDER} !important`,
  "&::before": {
    display: "none",
  },
  height: 56,
  "&.Mui-expanded": {
    height: "auto",
  },
};

const accordionSummarySx = {
  flexDirection: "row-reverse",
  height: "100%",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: (theme) => theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
};

const accordionDetailsSx = {
  padding: 2,
  borderTop: "1px solid rgba(0, 0, 0, .125)",
};

const SiteLinksContainer = ({
  control,
  errors,
  name = "sitelinks",
  label = SITE_LINKS_PLACEHOLDER,
  disabled = false,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const [expanded, setExpanded] = useState(null);
  const { t } = useTranslationApp();
  const sectionName = typeof name === "string" ? name.split(".").pop() : "";
  const aiBasePath =
    sectionName && name.endsWith(`.${sectionName}`)
      ? name.slice(0, -(sectionName.length + 1))
      : "";

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
  };

  return (
    <Box id={name}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
        <Typography
          variant="body1"
          sx={{ fontWeight: "semibold", fontSize: "18px" }}
        >
          {t(label)}
        </Typography>
        <ChipOptional />
        {aiBasePath ? (
          <ChipAIGeneratedSection basePath={aiBasePath} section={sectionName} />
        ) : null}
      </Box>

      {fields.map((field, index) => {
        const hasSomeError = getReactHookFormNestedError(
          errors,
          `${name}[${index}]`
        );
        return (
          <MuiAccordion
            key={field.id}
            disableGutters
            elevation={0}
            square
            expanded={expanded === index}
            onChange={handleAccordionChange(index)}
            sx={{
              ...accordionSx,
              backgroundColor: "fields.background",
              boxShadow: BOX_SHADOW_THEME,
              border: `1px solid ${
                hasSomeError ? RED_COLOR : INPUT_COLOR_BORDER
              } !important`,
              borderRadius: "10px !important",
              marginBottom: 2,
            }}
          >
            <MuiAccordionSummary
              expandIcon={
                <ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />
              }
              aria-controls={`sitelink-${index}-content`}
              id={`sitelink-${index}-header`}
              sx={accordionSummarySx}
            >
              <Typography>
                {field.linkText && field.linkText.trim() !== ""
                  ? field.linkText
                  : `${t(SITE_LINK_PLACEHOLDER)} ${index + 1}`}
              </Typography>
              {!disabled && (
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <IconButton
                    onClick={() => remove(index)}
                    type="button"
                    sx={{
                      p: 0,
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}
            </MuiAccordionSummary>
            <MuiAccordionDetails sx={accordionDetailsSx}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {t(SITE_LINK_TEXT_PLACEHOLDER)}
                  </Typography>
                  <TextField
                    name={`${name}[${index}].linkText`}
                    variant="filled"
                    fullWidth
                    control={control}
                    errors={errors}
                    disabled={disabled}
                    showCounterCharacters={true}
                    maxCharacters={
                      ADS_CAMPAIGN_GOOGLE_SITE_LINK_NAME_MAX_LENGTH
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {t(SITE_LINK_FINAL_URL_PLACEHOLDER)}
                  </Typography>
                  <TextField
                    name={`${name}[${index}].finalUrl`}
                    variant="filled"
                    fullWidth
                    control={control}
                    errors={errors}
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    {(field.descriptions || []).map((desc, descIndex) => (
                      <Grid item xs={12} sm={6} key={descIndex}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          {t(SITE_LINK_DESCRIPTION_PLACEHOLDER)} {descIndex + 1}
                        </Typography>
                        <TextField
                          name={`${name}[${index}].descriptions[${descIndex}]`}
                          variant="filled"
                          fullWidth
                          control={control}
                          errors={errors}
                          disabled={disabled}
                          showCounterCharacters={true}
                          maxCharacters={
                            ADS_CAMPAIGN_GOOGLE_SITE_LINK_DESCRIPTION_MAX_LENGTH
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
                <Grid item xs={12}></Grid>
              </Grid>
            </MuiAccordionDetails>
          </MuiAccordion>
        );
      })}
      {fields.length < 10 && !disabled && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <IconButton
            onClick={() =>
              append({
                linkText: "",
                finalUrl: "",
                descriptions: ["", ""],
              })
            }
            type="button"
            sx={{
              width: "100%",
              fontSize: "14px",
              backgroundColor: "transparent",
              color: "#FFF",
              border: "1px solid #000",
              borderRadius: BORDER_RADIUS,
              "&:hover": {
                color: "#FFF",
                backgroundColor: "transparent",
              },
            }}
          >
            <AddIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default SiteLinksContainer;
